import type { MuteLevel } from './types/index.js';

export type { MuteLevel } from './types/index.js';

/**
 * Notification-mute decision logic, shared so server, web, and mobile all
 * interpret a `MuteLevel` identically instead of each re-deriving the rules.
 *
 * Levels (see `MuteLevel`):
 *   ALL         — notify normally (the default; usually stored as "no row").
 *   ALL_SILENT  — still counts/banners, but no sound.
 *   MENTIONS    — only personally-relevant activity (DM / @-mention / reply-to-me).
 *   NOTHING     — fully silent: no unread bump, no notification.
 */

/**
 * Resolve the EFFECTIVE level for a channel from the user's mute rows: a
 * per-channel override wins, otherwise the channel's guild mute applies,
 * otherwise the default is ALL. DMs have no guild, so only the channel
 * override (or the default) is consulted. Mirrors the web resolver in
 * `client/src/lib/muteSettings.ts` (getChannelLevel).
 */
export function resolveMuteLevel(
  channelLevel?: MuteLevel | null,
  guildLevel?: MuteLevel | null,
): MuteLevel {
  return channelLevel ?? guildLevel ?? 'ALL';
}

/** What an incoming message signals, relative to the current user. */
export interface ActivitySignal {
  /** Direct message (no guild). */
  isDm: boolean;
  /** The message @-mentions me. */
  mentioned: boolean;
  /** The message replies to a message I authored. */
  repliedTo: boolean;
}

function isPersonallyRelevant(s: ActivitySignal): boolean {
  return s.isDm || s.mentioned || s.repliedTo;
}

/**
 * Does this activity "pass" the mute level — i.e. is it allowed to bump the
 * unread badge AND (subject to the caller's own base rules) raise a
 * notification?
 *   NOTHING   → never.
 *   MENTIONS  → only when personally relevant (DM / mention / reply-to-me).
 *   ALL / ALL_SILENT → always (ALL_SILENT differs only in sound, see allowsSound).
 */
export function passesMuteLevel(level: MuteLevel, s: ActivitySignal): boolean {
  if (level === 'NOTHING') return false;
  if (level === 'MENTIONS') return isPersonallyRelevant(s);
  return true;
}

/**
 * Within an allowed notification, should it make a SOUND (vs a silent banner)?
 * ALL_SILENT shows the banner but stays quiet. Assumes passesMuteLevel already
 * returned true.
 */
export function allowsSound(level: MuteLevel): boolean {
  return level !== 'ALL_SILENT';
}

/**
 * What a stored (server) unread count shows under a mute level — the hydrated
 * counterpart of {@link passesMuteLevel}, which filters one live message:
 *   NOTHING  → nothing;
 *   MENTIONS → in a server channel only the mentions count (a DM always passes);
 *   ALL / ALL_SILENT → everything.
 */
export function unreadUnderMuteLevel(
  level: MuteLevel,
  s: { isDm: boolean; unread: number; mentions: number },
): { unread: number; mentions: number } {
  if (passesMuteLevel(level, { isDm: s.isDm, mentioned: false, repliedTo: false })) {
    return { unread: s.unread, mentions: s.mentions };
  }
  if (passesMuteLevel(level, { isDm: s.isDm, mentioned: true, repliedTo: false })) {
    return { unread: s.mentions, mentions: s.mentions };
  }
  return { unread: 0, mentions: 0 };
}
